import React, { useEffect, useMemo, useState } from "react";
import GroupSubNav from "../../components/layout/GroupSubNav";

import { Helmet } from "react-helmet";
import * as Animate from "react-reveal";
import { Link } from "react-router-dom";
import Preloader from "../../components/blocks/Preloader";
import HeaderNavigation from "../../components/layout/HeaderNavigation";
import { getEventsApi } from "../../store/actions";
import { eventCategories, getEventCategory } from "../../utils/eventCategories";
import { scrollToTop } from "../../utils/scrollToTop";
import SiteFooter from "../../components/layout/SiteFooter";

const cardThemes = [
  { gradient: "linear-gradient(135deg,#050794,#2024c4)", ink: "#fff" },
  { gradient: "linear-gradient(135deg,#d88700,#fbaf1b)", ink: "#151863" },
  { gradient: "linear-gradient(135deg,#5131b8,#8b69e7)", ink: "#fff" },
  { gradient: "linear-gradient(135deg,#066b59,#17a487)", ink: "#fff" },
  { gradient: "linear-gradient(135deg,#b83850,#f17665)", ink: "#fff" },
  { gradient: "linear-gradient(135deg,#05648d,#28a8d7)", ink: "#fff" },
];

const STOPWORDS = new Set([
  "a",
  "an",
  "the",
  "of",
  "to",
  "in",
  "on",
  "for",
  "and",
  "is",
  "are",
  "how",
  "why",
  "who",
  "what",
  "when",
  "where",
  "your",
  "you",
  "cant",
  "with",
  "from",
  "this",
  "that",
  "be",
  "will",
  "still",
  "get",
  "my",
]);

const getCardWord = (title = "") => {
  const words = title
    .replace(/[^\w\s]/g, "")
    .split(/\s+/)
    .filter(Boolean);
  // Prefer the shortest meaningful word in the title (punchier as a big
  // display word) over just the first one, so a headline like "Why Smart
  // People Still Get Left Behind" renders "SMART" rather than a long word
  // that has to be truncated mid-syllable.
  const candidates = words
    .filter((w) => w.length >= 3 && !STOPWORDS.has(w.toLowerCase()))
    .sort((a, b) => a.length - b.length);
  const candidate = candidates[0] || words[0] || "EVENT";
  return candidate.toUpperCase().slice(0, 10);
};

const EventCard = ({ event, index }) => {
  const theme = cardThemes[index % cardThemes.length];
  const category = getEventCategory(event?.category);

  return (
    <article
      className="bg-white border border-[#dbe3f3] rounded-[20px] grid overflow-hidden transition-all duration-200 hover:-translate-y-[5px] hover:border-[#bcc9e6]"
      style={{
        gridTemplateRows: "178px 1fr",
        minHeight: "450px",
        boxShadow: "0 12px 32px rgba(23,29,65,0.07)",
      }}
    >
      <div
        className="relative overflow-hidden p-[22px]"
        style={{ background: theme.gradient, color: theme.ink }}
      >
        <span
          className="absolute rounded-full"
          style={{
            border: "1px solid rgba(255,255,255,0.28)",
            width: "190px",
            height: "190px",
            top: "-92px",
            right: "-66px",
          }}
          aria-hidden="true"
        />
        <span
          className="absolute rounded-full"
          style={{
            border: "1px solid rgba(255,255,255,0.28)",
            width: "112px",
            height: "112px",
            top: "-50px",
            right: "5px",
          }}
          aria-hidden="true"
        />
        <span
          className="absolute rounded-full"
          style={{
            border: "3px solid currentColor",
            width: "12px",
            height: "12px",
            top: "22px",
            left: "22px",
          }}
          aria-hidden="true"
        />
        <span
          className="absolute font-poppins font-extrabold"
          style={{
            color: "currentColor",
            opacity: 0.82,
            fontSize: "15px",
            top: "20px",
            right: "22px",
          }}
        >
          {String(index + 1).padStart(2, "0")}
        </span>
        <strong
          className="absolute font-poppins"
          style={{
            color: "currentColor",
            letterSpacing: "-0.055em",
            maxWidth: "78%",
            fontSize: "clamp(30px, 3.5vw, 48px)",
            lineHeight: 0.95,
            bottom: "20px",
            left: "22px",
          }}
        >
          {getCardWord(event?.title)}
        </strong>
        <span
          className="absolute rounded-full grid place-items-center"
          style={{
            color: "currentColor",
            background: "rgba(255,255,255,0.12)",
            border: "1px solid rgba(255,255,255,0.45)",
            width: "44px",
            height: "44px",
            fontSize: "19px",
            bottom: "18px",
            right: "20px",
          }}
          aria-hidden="true"
        >
          ↗
        </span>
      </div>

      <div className="flex flex-col items-start p-6">
        {category && (
          <p className="text-[#8c5b00] font-poppins tracking-[0.08em] uppercase text-[14px] font-extrabold mb-[10px]">
            {category.label}
          </p>
        )}
        <h3 className="text-primary font-poppins tracking-[-0.025em] text-[21px] leading-[1.36] mb-3">
          {event?.title}
        </h3>
        <span className="text-[#5a6277] font-poppins text-[15px] leading-[1.65] mb-[21px]">
          {event?.eventShortDescription}
        </span>
        <Link
          to={`/events/${event?.slug}`}
          className="mt-auto text-primary font-poppins inline-flex items-center gap-[9px] text-[15px] font-extrabold hover:text-[#03046b] transition"
          aria-label={`View details for ${event?.title}`}
        >
          View event details <span aria-hidden="true">↗</span>
        </Link>
      </div>
    </article>
  );
};

const Events = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");

  const fetchData = async (page, limit) => {
    setLoading(true);
    try {
      const params = { page, limit };
      const response = await getEventsApi(params);
      setEvents(response.data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  useEffect(() => {
    scrollToTop();
    fetchData(1, 100);
  }, []);

  const filteredEvents = useMemo(() => {
    const query = search.trim().toLowerCase();
    return (events || []).filter((event) => {
      const matchesCategory =
        activeCategory === "all" || event?.category === activeCategory;
      const matchesQuery =
        !query || event?.title?.toLowerCase().includes(query);
      return matchesCategory && matchesQuery;
    });
  }, [events, search, activeCategory]);

  const hasFilters = search.trim() !== "" || activeCategory !== "all";

  const resetFilters = () => {
    setSearch("");
    setActiveCategory("all");
  };

  return (
    <div>
      <Helmet>
        <title>
          Utiva Events | Workshops, Webinars &amp; Community Sessions
        </title>
        <meta
          name="description"
          content="Explore Utiva events, webinars, masterclasses and community sessions. Search and filter by topic."
        />
        <meta property="og:title" content="Utiva Events" />
        <meta
          property="og:description"
          content="Find practical sessions, career conversations and community events from Utiva."
        />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:type" content="website" />
      </Helmet>

      <header className="w-full relative">
        <HeaderNavigation />
      </header>
      <GroupSubNav />

      <main>
        <section
          className="grid events-grid-hero gap-7 lg:gap-[70px] items-center relative overflow-hidden border-b border-[#dce4f5] px-6 lg:px-8 py-10 lg:py-[54px]"
          style={{
            background:
              "radial-gradient(circle at 88% 16%, rgba(251,175,27,0.22) 0 6px, transparent 7px), radial-gradient(circle at 91% 30%, rgba(251,175,27,0.16) 0 4px, transparent 5px), linear-gradient(126deg,#f7f9ff 0%,#fff 59%,#fff8e8 100%)",
            minHeight: "315px",
          }}
        >
          <span
            className="absolute rounded-full hidden lg:block"
            style={{
              border: "54px solid rgba(5,7,148,0.055)",
              width: "410px",
              height: "410px",
              bottom: "-160px",
              right: "-100px",
            }}
            aria-hidden="true"
          />

          <Animate.Fade bottom>
            <div className="relative z-[1] max-w-[790px]">
              <p className="flex items-center gap-3 text-primary font-poppins tracking-[0.11em] uppercase text-[15px] font-extrabold mb-[14px]">
                <span className="w-[34px] h-[3px] bg-secondary inline-block shrink-0"></span>
                Utiva Events
              </p>
              <h1
                className="text-primary font-poppins tracking-[-0.055em] leading-[1.03] font-black mb-4"
                style={{ fontSize: "clamp(43px, 5vw, 68px)" }}
              >
                Find your next{" "}
                <span
                  className="relative inline-block"
                  style={{
                    background: "linear-gradient(transparent 72%, #fbaf1b 0)",
                  }}
                >
                  room to grow.
                </span>
              </h1>
              <p className="text-[#5a6277] font-poppins max-w-[750px] text-[18px] leading-[1.75] m-0">
                Explore practical sessions, honest career conversations and live
                community experiences—then choose what moves you forward.
              </p>
            </div>
          </Animate.Fade>

          <Animate.Fade bottom>
            <div
              className="relative z-[1] rounded-[20px] p-6"
              style={{
                background: "rgba(255,255,255,0.86)",
                border: "1px solid #d8e0f2",
                backdropFilter: "blur(10px)",
                WebkitBackdropFilter: "blur(10px)",
                boxShadow: "0 20px 60px rgba(21,30,81,0.11)",
              }}
            >
              <label
                htmlFor="event-search"
                className="text-primary font-poppins text-[16px] font-extrabold mb-[10px] block"
              >
                Search events
              </label>
              <div
                className="bg-white flex items-center gap-[11px] rounded-[13px] px-4"
                style={{
                  border: "2px solid #cdd7ee",
                  minHeight: "58px",
                }}
              >
                <span
                  aria-hidden="true"
                  className="text-primary"
                  style={{
                    fontSize: "28px",
                    lineHeight: 1,
                    transform: "rotate(-15deg)",
                  }}
                >
                  ⌕
                </span>
                <input
                  id="event-search"
                  type="search"
                  placeholder="Try AI, product, careers..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full min-w-0 text-[#181b2a] bg-transparent border-0 outline-none font-poppins text-[16px]"
                />
              </div>
              <a
                href="mailto:info@utiva.io?subject=Add%20me%20to%20Utiva%20event%20updates"
                className="text-primary font-poppins inline-flex items-center gap-2 mt-[15px] text-[15px] font-extrabold hover:text-[#03046b] transition"
              >
                Get event updates <span aria-hidden="true">↗</span>
              </a>
            </div>
          </Animate.Fade>
        </section>

        <section
          aria-labelledby="event-catalogue-title"
          className="mx-auto px-6 lg:px-[26px] pt-8 lg:pt-10 pb-16 lg:pb-[88px]"
          style={{ maxWidth: "1280px" }}
        >
          <div
            className="z-[12] rounded-[18px] mb-7 lg:mb-[30px] px-5 lg:px-6 pt-5 lg:pt-[23px] pb-5"
            style={{
              border: "1px solid #dce4f5",
              background: "rgba(255,255,255,0.95)",
              backdropFilter: "blur(14px)",
              WebkitBackdropFilter: "blur(14px)",
              boxShadow: "0 14px 34px rgba(25,33,75,0.08)",
              position: "sticky",
              top:
                "calc(var(--header-height, 90px) + var(--subnav-height, 68px))",
            }}
          >
            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-end gap-4 lg:gap-6 mb-[18px]">
              <div>
                <p className="text-[#8c5b00] font-poppins tracking-[0.08em] uppercase text-[13px] lg:text-[14px] font-extrabold mb-1">
                  Browse the calendar
                </p>
                <h2
                  id="event-catalogue-title"
                  className="text-primary font-poppins tracking-[-0.035em] m-0"
                  style={{
                    fontSize: "clamp(24px, 2.6vw, 36px)",
                    lineHeight: 1.18,
                  }}
                >
                  Events worth showing up for.
                </h2>
              </div>
              <span className="flex-none text-primary bg-[#edf2ff] rounded-full px-[13px] py-2 text-[14px] lg:text-[15px] font-poppins font-extrabold">
                {filteredEvents.length} events
              </span>
            </div>

            <div
              className="flex gap-2 overflow-x-auto no-scrollbar"
              aria-label="Filter events by topic"
            >
              <button
                type="button"
                onClick={() => setActiveCategory("all")}
                aria-pressed={activeCategory === "all"}
                className={`flex-none rounded-full font-poppins text-[15px] font-bold px-[14px] transition-all ${
                  activeCategory === "all"
                    ? "bg-primary border-primary text-white"
                    : "bg-[#f8faff] border-[#d5ddef] text-[#4c5365] hover:border-primary hover:text-primary hover:-translate-y-px"
                }`}
                style={{ border: "1px solid", minHeight: "43px" }}
              >
                All events
              </button>
              {eventCategories.map((category) => (
                <button
                  key={category.value}
                  type="button"
                  onClick={() => setActiveCategory(category.value)}
                  aria-pressed={activeCategory === category.value}
                  className={`flex-none rounded-full font-poppins text-[15px] font-bold px-[14px] transition-all whitespace-nowrap ${
                    activeCategory === category.value
                      ? "bg-primary border-primary text-white"
                      : "bg-[#f8faff] border-[#d5ddef] text-[#4c5365] hover:border-primary hover:text-primary hover:-translate-y-px"
                  }`}
                  style={{ border: "1px solid", minHeight: "43px" }}
                >
                  {category.label}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <Preloader />
          ) : filteredEvents.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-[17px] lg:gap-[22px]">
              {filteredEvents.map((event, index) => (
                <Animate.Fade bottom key={event._id}>
                  <EventCard event={event} index={index} />
                </Animate.Fade>
              ))}
            </div>
          ) : (
            <div
              className="text-center grid place-items-center rounded-[20px]"
              style={{
                background: "#f7f9ff",
                border: "1px dashed #b8c4df",
                minHeight: "360px",
                padding: "50px 24px",
              }}
            >
              <span
                className="text-secondary block mb-3"
                style={{ fontSize: "48px", transform: "rotate(-15deg)" }}
              >
                ⌕
              </span>
              <h3 className="text-primary font-poppins text-[27px] mb-[7px]">
                {events.length === 0
                  ? "No upcoming events right now"
                  : "No events match your search"}
              </h3>
              <p className="text-[#5a6277] font-poppins text-[16px] leading-[1.65] max-w-[560px] mb-5">
                {events.length === 0
                  ? "Check back soon, or ask to be added to our event updates below."
                  : "Try a different topic or search term."}
              </p>
              {hasFilters && (
                <button
                  type="button"
                  onClick={resetFilters}
                  className="bg-primary text-white rounded-[11px] font-poppins text-[15px] font-extrabold px-[18px]"
                  style={{ minHeight: "48px" }}
                >
                  Reset filters
                </button>
              )}
            </div>
          )}
        </section>

        <section className="px-4">
          <Animate.Fade bottom>
            <div
              className="bg-primary text-white rounded-[25px] flex flex-col lg:flex-row justify-between items-start lg:items-center gap-8 lg:gap-12 relative overflow-hidden mx-auto"
              style={{
                maxWidth: "1228px",
                minHeight: "230px",
                marginBottom: "88px",
                padding: "34px 26px",
              }}
            >
              <span
                className="absolute font-poppins font-extrabold pointer-events-none hidden md:block"
                style={{
                  color: "rgba(255,255,255,0.07)",
                  letterSpacing: "-0.07em",
                  fontSize: "112px",
                  bottom: "-39px",
                  right: "-10px",
                }}
                aria-hidden="true"
              >
                EVENTS
              </span>
              <div className="relative z-[1]">
                <p className="text-secondary font-poppins tracking-[0.1em] uppercase text-[14px] font-extrabold mb-2">
                  Don't Miss The Next Room
                </p>
                <h2
                  className="font-poppins tracking-[-0.045em] max-w-[720px] m-0"
                  style={{
                    fontSize: "clamp(29px, 3.5vw, 46px)",
                    lineHeight: 1.18,
                  }}
                >
                  New conversations. New people. Better possibilities.
                </h2>
              </div>
              <a
                href="mailto:info@utiva.io?subject=Add%20me%20to%20Utiva%20event%20updates"
                className="relative z-[1] bg-secondary text-primary rounded-xl inline-flex items-center gap-[10px] font-poppins text-[15px] font-extrabold px-5 shrink-0"
                style={{ minHeight: "54px" }}
              >
                Send me event updates <span aria-hidden="true">→</span>
              </a>
            </div>
          </Animate.Fade>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
};

export default Events;
